import { Select, notification } from "antd";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { getEnvironmentalDataForBounds, goToMapPosition, setEnvironmentalDataFilters, setEnvironmentalDataSelectedSegment } from "redux/actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Highlighter from "react-highlight-words";
import { centroid } from "@turf/turf";

import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons";
import OBCSearchInput from "./OBC/OBCSearchInput";
import OBCSpinner from "./util/OBC";

const selectedSegmentSelector = (state) => state.environmentData.selectedSegment;
const environmentDataTypesSelector = (state) => state.environmentData.types;
const environmentDataSubTypesSelector = (state) => state.environmentData.subTypes;
const environmentDataSubFiltersSelector = (state) => state.environmentData.filters;
const environmentDataMapDataSelector = (state) => state.environmentData.mapData;
const environmentDataLoadedTypesSelector = (state) => state.environmentData.dataLoadedTypes;
const environmentDataErrorLoadingSelector = (state) => state.environmentData.errorLoading;

// below values might have to be changed if more data added to the database (also will have to be updated in EnvironmentalPolygons.js)
const KEYS_PER_DATA_TYPE = {
    vegetation: [
        "data_type",
        "sub_data_type",
        "route_id",
        "full_data.display_fields.Vegetation Density Level",
        "full_data.display_fields.Vegetation Encroachment Status",
        "full_data.vegetation_density_percentage",
        "full_data.corridor_width",
        "start_location",
        "end_location",
    ],
    land_movement: [
        "data_type",
        "sub_data_type",
        "route_id",
        "full_data.display_fields.Land Movement Average Velocity",
        "full_data.display_fields.Land Movement Velocity Rate of Change",
        "full_data.velocity_rate_of_change",
        "full_data.number_of_points",
        "full_data.average_velocity",
        "full_data.corridor_width",
        "start_location",
        "end_location",
    ],
    flood_risk: [
        "data_type",
        "sub_data_type",
        "route_id",
        "full_data.display_fields.Fluvial Flood Risk",
        "full_data.display_fields.Pluvial Flood Risk",
        "full_data.extra_fields.max_flood_depth_cm",
        "full_data.corridor_width",
        "start_location",
        "end_location",
    ],
};

const SatelliteDataTab = () => {
    const dispatch = useDispatch();

    const selectedSegment = useSelector(selectedSegmentSelector);
    const environmentDataTypes = useSelector(environmentDataTypesSelector);
    const environmentDataSubTypes = useSelector(environmentDataSubTypesSelector);
    const environmentDataSubFilters = useSelector(environmentDataSubFiltersSelector);
    const environmentDataMapData = useSelector(environmentDataMapDataSelector);
    const environmentDataLoadedTypes = useSelector(environmentDataLoadedTypesSelector);
    const environmentDataErrorLoading = useSelector(environmentDataErrorLoadingSelector);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRouteSection, setSelectedRouteSection] = useState(null);

    const filterType = useMemo(() => {
        return _.get(environmentDataSubFilters, "type");
    }, [environmentDataSubFilters]);

    const filterSubType = useMemo(() => {
        return _.get(environmentDataSubFilters, "subType");
    }, [environmentDataSubFilters]);

    const filteredSubTypes = useMemo(() => {
        const newList = _.filter(environmentDataSubTypes, (subType) => _.includes(subType, filterType));
        return newList;
    }, [environmentDataSubTypes, filterType]);

    const isCurrentDataTypeLoaded = useMemo(() => {
        const isLoaded = _.get(environmentDataLoadedTypes, [filterType, filterSubType]);
        return isLoaded;
    }, [environmentDataLoadedTypes, filterSubType, filterType]);

    useEffect(() => {
        if (!isCurrentDataTypeLoaded) {
            dispatch(getEnvironmentalDataForBounds(0, 0, filterType, filterSubType));
        }
    }, [dispatch, filterSubType, filterType, isCurrentDataTypeLoaded]);

    // when user changes the data type we need to reselect the sub type (default to the first position)
    const changeFilterType = (value) => {
        dispatch(setEnvironmentalDataSelectedSegment({}));
        const firstValidSubType = _.get(
            _.filter(environmentDataSubTypes, (subType) => _.includes(subType, value)),
            0,
        );
        dispatch(setEnvironmentalDataFilters({ type: value, subType: firstValidSubType }));
    };

    // when user change the data sub type clear currently selected
    const changeFilterSubType = (value) => {
        dispatch(setEnvironmentalDataSelectedSegment({}));
        dispatch(setEnvironmentalDataFilters({ subType: value }));
    };

    // below to optimise the amount od rerenders
    useEffect(() => {
        const currentRouteID = _.get(selectedSegment, "route_id");
        const selectedRouteID = _.get(selectedRouteSection, "route_id");
        if (selectedRouteID !== currentRouteID) {
            setSelectedRouteSection(selectedSegment);
        }
    }, [selectedRouteSection, selectedSegment]);

    const calculateStartMileAndChain = (position) => {
        const start_mile = Math.floor(position / 80);
        const start_chain = position % 80;
        return `MILE: ${start_mile} CHAIN: ${start_chain}`;
    };

    const selectedEnvironmentDataMapData = useMemo(() => {
        let _data = {};
        if (isCurrentDataTypeLoaded) {
            _data = _.get(environmentDataMapData, [filterType, filterSubType]);
        }
        return _data;
    }, [environmentDataMapData, filterSubType, filterType, isCurrentDataTypeLoaded]);

    const selectedSegmentRouteID = useMemo(() => _.get(selectedRouteSection, "route_id", null), [selectedRouteSection]);

    // get all features for given ELR
    const selectedRouteIdFeatures = useMemo(() => {
        let newList = _.map(
            _.filter(selectedEnvironmentDataMapData, (data) => _.get(data, ["properties", "route_id"], null) === selectedSegmentRouteID),
            (data, index) => {
                return { ...data, ...{ id: index } };
            },
        );

        newList = _.orderBy(newList, ["properties.start_location"], ["asc"]);
        return newList;
    }, [selectedEnvironmentDataMapData, selectedSegmentRouteID]);

    // this returns a list of all ELR's for selected data type & data subtype
    const filteredElrsList = useMemo(() => {
        // prevent from running this if not all envData loaded yet
        const isTypeLoaded = _.get(environmentDataLoadedTypes, [filterType, filterSubType]);

        if (!isTypeLoaded) {
            return null;
        }

        const groupedByELR = selectedEnvironmentDataMapData.reduce((result, currentItem) => {
            const elr = currentItem.properties.route_id;

            // If the group for the current ELR doesn't exist, create it
            if (!result[elr]) {
                result[elr] = [];
            }

            // Add the current item to the group
            result[elr].push(currentItem.properties);

            return result;
        }, {});

        const sortedKeys = Object.keys(groupedByELR).sort();
        const sortedObject = {};

        _.map(sortedKeys, (key) => {
            let includeElr = true;
            if (searchQuery && searchQuery.length > 0) {
                if (String(key).toLowerCase().indexOf(searchQuery.toLowerCase()) === -1) {
                    includeElr = false;
                }
            }

            if (includeElr) {
                sortedObject[key] = groupedByELR[key];
            }
        });

        return sortedObject;
    }, [environmentDataLoadedTypes, selectedEnvironmentDataMapData, filterSubType, filterType, searchQuery]);

    // Update position of the map based on new segment coordinates
    const updateMapPosition = useCallback(
        (geojsonPolygon) => {
            // this will calculate the center of the polygon so we can set map position to the center of the polygon
            const centroidCoords = _.get(centroid(geojsonPolygon), ["geometry", "coordinates"]);

            if (centroidCoords) {
                dispatch(goToMapPosition([centroidCoords[1], centroidCoords[0], 15]));
            }
        },
        [dispatch],
    );

    // navigate to the next segment if exists, otherwise display notification info that this is last segment for this ELR line
    const goToNextSegment = useCallback(() => {
        const selectedSegmentID = _.get(selectedSegment, "id");
        const selectedSegmentIndex = _.findIndex(selectedRouteIdFeatures, { properties: { id: selectedSegmentID } });
        const nextSegment = _.get(selectedRouteIdFeatures, [selectedSegmentIndex + 1, "properties"]);
        const nextSegmentCoords = _.get(selectedRouteIdFeatures, [selectedSegmentIndex + 1, "geometry"]);

        if (nextSegment && nextSegmentCoords) {
            dispatch(setEnvironmentalDataSelectedSegment(nextSegment));
            updateMapPosition(nextSegmentCoords);
        } else {
            notification.info({
                message: "Last segment",
                description: "You are currently on the last segment in this ELR section",
            });
        }
    }, [selectedSegment, selectedRouteIdFeatures, dispatch, updateMapPosition]);

    // navigate to the previous segment if exists, otherwise display notification info that this is last segment for this ELR line
    const goToPreviousSegment = useCallback(() => {
        const selectedSegmentID = _.get(selectedSegment, "id");
        const selectedSegmentIndex = _.findIndex(selectedRouteIdFeatures, { properties: { id: selectedSegmentID } });
        const prevSegment = _.get(selectedRouteIdFeatures, [selectedSegmentIndex - 1, "properties"]);
        const prevSegmentCoords = _.get(selectedRouteIdFeatures, [selectedSegmentIndex - 1, "geometry"]);
        if (prevSegment && prevSegmentCoords) {
            dispatch(setEnvironmentalDataSelectedSegment(prevSegment));
            updateMapPosition(prevSegmentCoords);
        } else {
            notification.info({
                message: "First segment",
                description: "You are currently on first segment in this ELR section",
            });
        }
    }, [selectedSegment, selectedRouteIdFeatures, dispatch, updateMapPosition]);

    // generate selected segment info which is displayed in the left below filters and nav buttons
    const selectedSegmentInfo = useMemo(() => {
        if (!_.isEmpty(selectedSegment)) {
            const dataTypeKeys = _.get(KEYS_PER_DATA_TYPE, filterType, []);

            let startEndLocations = { start: null, end: null };

            return (
                <div className="SatelliteDataTabInfoContainer">
                    <div className="SatelliteDataTabInfoContainerTitle">Segment details:</div>
                    {_.map(dataTypeKeys, (key, index) => {
                        const keysList = _.split(key, ".");
                        const label = _.startCase(keysList.at(-1));
                        let value = _.get(selectedSegment, keysList, null);

                        if (keysList.at(-1) === "vegetation_density_percentage") {
                            value = `${_.floor(value, 2)}%`;
                        }

                        if (["data_type", "sub_data_type"].includes(keysList.at(-1))) {
                            value = _.startCase(value);
                        }

                        if (["start_location"].includes(keysList.at(0))) {
                            value = calculateStartMileAndChain(value);
                            startEndLocations.start = value;
                            value = null;
                        }
                        if (["end_location"].includes(keysList.at(0))) {
                            value = calculateStartMileAndChain(value);
                            startEndLocations.end = value;
                            value = null;
                        }

                        if (value) {
                            return (
                                <div
                                    key={index}
                                    className="SatelliteDataTabInfoContainerItem">
                                    <div className="SatelliteDataTabInfoContainerItemLabel">{label}:</div>
                                    <div className="SatelliteDataTabInfoContainerItemValue">{value}</div>
                                </div>
                            );
                        }
                    })}
                    {startEndLocations.start && startEndLocations.end ? (
                        <div className="SatelliteDataTabInfoContainerItem">
                            <div className="SatelliteDataTabInfoContainerItemLabel">Location:</div>
                            <div className="SatelliteDataTabInfoContainerItemValue">
                                {startEndLocations.start} - {startEndLocations.end}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        }

        if (!isCurrentDataTypeLoaded) {
            return (
                <div className="SatelliteDataTabInfoLoadingContainer">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }

        return (
            <div className="SatelliteDataTabInfoContainer">
                <div className="SatelliteDataTabInfoContainerCenterInfo">Select segment on the map or ELR from the list</div>
            </div>
        );
    }, [isCurrentDataTypeLoaded, selectedSegment, filterType]);

    // this will find and select first element for selected ELR from the list on the right
    const selectFirstSegment = useCallback(
        (selectedRouteIdFeatures) => {
            let firstElementID = _.get(selectedRouteIdFeatures, [0, "id"]);
            const firstFeatureIndex = _.findIndex(selectedEnvironmentDataMapData, { properties: { id: firstElementID } });
            const firstRouteIdFeature = _.get(selectedEnvironmentDataMapData, firstFeatureIndex);

            const properties = _.get(firstRouteIdFeature, "properties");
            const geometry = _.get(firstRouteIdFeature, "geometry");

            if (properties && geometry) {
                dispatch(setEnvironmentalDataSelectedSegment(properties));
                updateMapPosition(geometry);
            }
        },
        [dispatch, selectedEnvironmentDataMapData, updateMapPosition],
    );

    // search query to be fired when user start typing in the search input
    const updateFilterSearchQuery = (value) => {
        dispatch(setEnvironmentalDataFilters({ searchQuery: value }));
    };

    // this will also get fired at the same time as above update but with debounce to improve performance
    const setFilterSearchQuery = useCallback(
        _.debounce((value) => {
            updateFilterSearchQuery(value);
        }, 250),
        [],
    );

    // will be called when user press X icon next to the nav buttons
    const clearSelectedSection = () => {
        dispatch(setEnvironmentalDataSelectedSegment({}));
    };

    // returns ELR name eg. CWL2
    const selectedElr = useMemo(() => {
        const currentSegmentElr = _.get(selectedRouteSection, "route_id");
        let selectedElr = _.get(filteredElrsList, currentSegmentElr, {});
        selectedElr = _.groupBy(selectedElr, "route_id");
        selectedElr = _.findKey(selectedElr);

        return selectedElr;
    }, [filteredElrsList, selectedRouteSection]);

    // returns null or current segment out of all segments number (1 out of 100)
    const segmentIndexLabel = useMemo(() => {
        const outOfSegments = Object.keys(selectedRouteIdFeatures).length;

        if (!selectedSegment || !outOfSegments) {
            return null;
        }

        const selectedSegmentID = _.get(selectedSegment, "id");
        const selectedSegmentIndex = _.findIndex(selectedRouteIdFeatures, { properties: { id: selectedSegmentID } });

        return `${selectedSegmentIndex + 1} out of ${outOfSegments}`;
    }, [selectedRouteIdFeatures, selectedSegment]);

    return (
        <div className="SatelliteDataTabMain">
            {environmentDataErrorLoading && !selectedEnvironmentDataMapData.length ? (
                <div className="SatelliteDataTabMainErrorMessage">There was an error loading data, please try refreshing a page.</div>
            ) : (
                <>
                    <div className="SatelliteDataTabFilterContainer">
                        <div className="LeftCol">
                            <div className="SatelliteDataTabFilterItem">
                                <span className="label">Type:</span>
                                <Select
                                    className="SatelliteDataTabFilter"
                                    dropdownMatchSelectWidth={false}
                                    size="small"
                                    value={filterType}
                                    onChange={changeFilterType}>
                                    {_.map(environmentDataTypes, (type) => {
                                        return (
                                            <Select.Option
                                                key={type}
                                                value={type}>
                                                {_.startCase(type)}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </div>

                            <div className="SatelliteDataTabFilterItem">
                                <span className="label">Sub Type:</span>
                                <Select
                                    className="SatelliteDataTabFilter"
                                    size="small"
                                    dropdownMatchSelectWidth={false}
                                    value={filterSubType}
                                    onChange={changeFilterSubType}>
                                    {_.map(filteredSubTypes, (type) => {
                                        return (
                                            <Select.Option
                                                key={type}
                                                value={type}>
                                                {_.startCase(type)}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </div>
                        </div>

                        <div className="RightCol">
                            <OBCSearchInput
                                value={searchQuery}
                                onChange={(value) => {
                                    setSearchQuery(value);
                                    setFilterSearchQuery(value);
                                }}
                                placeholder="Search for ELR"
                            />
                        </div>
                    </div>
                    <div className="SatelliteDataTabMainBottom">
                        <div className="SatelliteDataTabMainLeft">
                            {segmentIndexLabel ? (
                                <div className="SatelliteDataTabMainLeftNavigation">
                                    <div className="markerControls">
                                        <div
                                            className="markerControls__Item"
                                            onClick={goToPreviousSegment}>
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </div>
                                        <div>{segmentIndexLabel}</div>
                                        <div
                                            className="markerControls__Item"
                                            onClick={goToNextSegment}>
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className="closeButton"
                                            onClick={clearSelectedSection}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {environmentDataErrorLoading && selectedEnvironmentDataMapData.length ? (
                                <div>There was an problem loading data, some of the data will be missing!</div>
                            ) : null}
                            {selectedSegmentInfo}
                        </div>
                        <div className="SatelliteDataTabMainRight">
                            <div className="SatelliteDataTabMainRightFilters"></div>
                            <div className="SatelliteDataTabMainRightMain">
                                <div className="SatelliteDataTabMainRightList">
                                    {!filteredElrsList ? (
                                        <div className="SatelliteDataTabMainRightListLoading">
                                            Loading data... {filterType === "flood_risk" ? <div>Flood will take longer then other types. </div> : ""}
                                        </div>
                                    ) : !Object.keys(filteredElrsList).length && searchQuery.length ? (
                                        <div>
                                            No results for <strong style={{ color: "#F8C46D" }}>{searchQuery}</strong>
                                        </div>
                                    ) : (
                                        _.map(filteredElrsList, (elrSegments, elr) => {
                                            return (
                                                <div
                                                    key={elr}
                                                    className={`SatelliteDataTabMainRightListItem ${selectedElr === elr ? "active" : ""}`}
                                                    onClick={() => selectFirstSegment(elrSegments)}>
                                                    <div>
                                                        ELR:{" "}
                                                        <strong>
                                                            <Highlighter
                                                                highlightClassName="ObcTextHighlight"
                                                                searchWords={searchQuery.split(" ")}
                                                                autoEscape={true}
                                                                textToHighlight={elr}
                                                            />
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        Total Segments: <strong>{Object.keys(elrSegments).length}</strong>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SatelliteDataTab;
